import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicOnlyRoute } from './PublicOnlyRoute';
import { RequireEmailVerification } from './RequireEmailVerification';
import { RequireRole } from './RequireRole';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { ROUTES } from './routeConfig';
import { ScrollToTop } from '../components/ScrollToTop';
import {
  // Public pages
  HomePage,
  AboutPage,
  PricingPage,
  PrivacyPage,
  TermsPage,
  ContactPage,
  // Auth pages
  SignInPage,
  SignUpPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  OAuthCallbackPage,
  // Invitation pages
  InvitationAcceptPage,
  // Shared pages
  SharedReportViewerPage,
  // Dashboard pages
  DashboardPage,
  ProfilePage,
  WorkspacesPage,
  PostsPage,
  CalendarPage,
  MediaPage,
  CampaignsPage,
  InboxPage,
  MentionsPage,
  AnalyticsPage,
  KnowledgePage,
  KnowledgeDocumentDetailPage,
  BrandVoicePage,
  BrandVoiceFormPage,
  PersonasPage,
  PersonaFormPage,
  AgentsDashboardPage,
  AgentConfigPage,
  AgentLogsPage,
  SettingsPage,
  ChannelsPage,
  // Settings sub-pages
  TeamPage,
  // Profile tab pages
  ProfileGeneralTab,
  ProfileSecurityTab,
  ProfilePreferencesTab,
  ProfileSessionsTab,
  ProfileDataPrivacyTab,
  // Error pages
  NotFoundPage,
} from '../pages';
import { WorkspacesPageActions } from '../pages/dashboard/WorkspacesPageActions';
import { TeamPageActions } from '../pages/dashboard/settings/TeamPageActions';
import { ChannelsPageActions } from '../pages/dashboard/ChannelsPageActions';
import { KnowledgePageActions } from '../pages/dashboard/KnowledgePageActions';
import { BrandVoicePageActions } from '../pages/dashboard/BrandVoicePageActions';
import { PersonasPageActions } from '../pages/dashboard/PersonasPageActions';
import { CampaignsPageActions } from '../pages/dashboard/CampaignsPageActions';
import { ContentPageActions } from '../pages/dashboard/ContentPageActions';
import { ReportsPageActions } from '../pages/dashboard/ReportsPageActions';
import { AdManagerPageActions } from '../pages/dashboard/AdManagerPageActions';
import { DashboardSkeleton } from '../pages/dashboard/components/DashboardSkeleton';

/**
 * Lazy-loaded MFA Challenge page
 */
const MfaChallengePage = lazy(() => import('../pages/auth/MfaChallengePage'));

/**
 * Lazy-loaded Persona Detail page
 */
const PersonaDetailPage = lazy(() => import('../pages/dashboard/personas/PersonaDetailPage'));

/**
 * Lazy-loaded Campaign Create Selection page
 */
const CampaignCreateSelectionPage = lazy(() => import('../pages/dashboard/campaigns/CampaignCreateSelectionPage'));

/**
 * Lazy-loaded Campaign Create page
 */
const CampaignCreatePage = lazy(() => import('../pages/dashboard/campaigns/CampaignCreatePage'));

/**
 * Lazy-loaded AI Campaign Create page
 */
const AICampaignCreatePage = lazy(() => import('../pages/dashboard/campaigns/AICampaignCreatePage'));

/**
 * Lazy-loaded Campaign Detail page
 */
const CampaignDetailPage = lazy(() => import('../pages/dashboard/campaigns/CampaignDetailPage'));

/**
 * Lazy-loaded Campaign Edit page
 */
const CampaignEditPage = lazy(() => import('../pages/dashboard/campaigns/CampaignEditPage'));

/**
 * Lazy-loaded Campaign Comparison page
 */
const CampaignComparisonPage = lazy(() => import('../pages/dashboard/campaigns/CampaignComparisonPage'));

/**
 * Lazy-loaded Content page
 */
const ContentPage = lazy(() => import('../pages/dashboard/content/ContentPage'));

/**
 * Lazy-loaded Content Create page
 */
const ContentCreatePage = lazy(() => import('../pages/dashboard/content/ContentCreatePage'));

/**
 * Lazy-loaded Content Detail page
 */
const ContentDetailPage = lazy(() => import('../pages/dashboard/content/ContentDetailPage'));

/**
 * Lazy-loaded Approval Queue page
 */
const ApprovalQueuePage = lazy(() => import('../pages/dashboard/content/ApprovalQueuePage'));

/**
 * Lazy-loaded Publishing page
 */
const PublishingPage = lazy(() => import('../pages/dashboard/publishing/PublishingPage'));

/**
 * Lazy-loaded Trends page
 */
const TrendsPage = lazy(() => import('../pages/dashboard/trends/TrendsPage'));

/**
 * Lazy-loaded Playbooks page
 */
const PlaybooksPage = lazy(() => import('../pages/dashboard/trends/PlaybooksPage'));

/**
 * Lazy-loaded Playbook Form page
 */
const PlaybookFormPage = lazy(() => import('../pages/dashboard/trends/PlaybookFormPage'));

/**
 * Lazy-loaded Crisis List page
 */
const CrisisListPage = lazy(() => import('../pages/dashboard/trends/CrisisListPage'));

/**
 * Lazy-loaded Crisis Dashboard page
 */
const CrisisDashboardPage = lazy(() => import('../pages/dashboard/trends/CrisisDashboardPage'));

/**
 * Lazy-loaded Trend Detail page
 */
const TrendDetailPage = lazy(() => import('../pages/dashboard/trends/TrendDetailPage'));

/**
 * Lazy-loaded Trend History page
 */
const TrendHistoryPage = lazy(() => import('../pages/dashboard/trends/TrendHistoryPage'));

/**
 * Lazy-loaded Reports page
 */
const ReportsPage = lazy(() => import('../pages/dashboard/reports/ReportsPage'));

/**
 * Lazy-loaded Report Builder page
 */
const ReportBuilderPage = lazy(() => import('../pages/dashboard/reports/ReportBuilderPage'));

/**
 * Lazy-loaded Report Viewer page
 */
const ReportViewerPage = lazy(() => import('../pages/dashboard/reports/ReportViewerPage'));

/**
 * Lazy-loaded Notification Center page
 */
const NotificationCenterPage = lazy(() => import('../pages/dashboard/notifications/NotificationCenterPage'));

/**
 * Lazy-loaded Notification Preferences page
 */
const NotificationPreferencesPage = lazy(() => import('../pages/dashboard/notifications/NotificationPreferencesPage'));

/**
 * Lazy-loaded Admin page
 */
const AdminPage = lazy(() => import('../pages/dashboard/admin/AdminPage'));

/**
 * Lazy-loaded Approval Workflows page
 */
const ApprovalWorkflowsPage = lazy(() => import('../pages/dashboard/admin/ApprovalWorkflowsPage'));

/**
 * Lazy-loaded Approval Workflow Form page
 */
const ApprovalWorkflowFormPage = lazy(() => import('../pages/dashboard/admin/ApprovalWorkflowFormPage'));

/**
 * Lazy-loaded Security Settings page
 */
const SecuritySettingsPage = lazy(() => import('../pages/dashboard/settings/SecuritySettingsPage'));

/**
 * Lazy-loaded Billing page
 */
const BillingPage = lazy(() => import('../pages/dashboard/settings/BillingPage'));

/**
 * Lazy-loaded Data Governance page
 */
const DataGovernancePage = lazy(() => import('../pages/dashboard/admin/DataGovernancePage'));

/**
 * Lazy-loaded API Keys page
 */
const APIKeysPage = lazy(() => import('../pages/dashboard/admin/api-keys/APIKeysPage'));

/**
 * Lazy-loaded Webhooks page
 */
const WebhooksPage = lazy(() => import('../pages/dashboard/admin/webhooks/WebhooksPage'));

/**
 * Lazy-loaded Ad Manager page
 */
const AdManagerPage = lazy(() => import('../pages/dashboard/ad-manager/AdManagerPage'));

/**
 * Lazy-loaded Ad Accounts page (shell — to be implemented in Task 19)
 */
const AdAccountsPage = lazy(() => import('../pages/dashboard/ad-manager/AdAccountsPage'));

/**
 * Lazy-loaded Ad Account Detail page (shell — to be implemented in Task 19)
 */
const AdAccountDetailPage = lazy(() => import('../pages/dashboard/ad-manager/AdAccountDetailPage'));

/**
 * Lazy-loaded Ad Campaigns page (shell — to be implemented in Task 20)
 */
const AdCampaignsPage = lazy(() => import('../pages/dashboard/ad-manager/AdCampaignsPage'));

/**
 * Lazy-loaded Ad Campaign Create page (shell — to be implemented in Task 20)
 */
const AdCampaignCreatePage = lazy(() => import('../pages/dashboard/ad-manager/AdCampaignCreatePage'));

/**
 * Lazy-loaded Ad Campaign Detail page (shell — to be implemented in Task 20)
 */
const AdCampaignDetailPage = lazy(() => import('../pages/dashboard/ad-manager/AdCampaignDetailPage'));

/**
 * Lazy-loaded Ad Campaign Edit page (shell — to be implemented in Task 20)
 */
const AdCampaignEditPage = lazy(() => import('../pages/dashboard/ad-manager/AdCampaignEditPage'));

/**
 * Lazy-loaded Ad Set Create page
 */
const AdSetCreatePage = lazy(() => import('../pages/dashboard/ad-manager/AdSetCreatePage'));

/**
 * Lazy-loaded Ad Set Detail page (shell — to be implemented in Task 21)
 */
const AdSetDetailPage = lazy(() => import('../pages/dashboard/ad-manager/AdSetDetailPage'));

/**
 * Lazy-loaded Ad Detail page (shell — to be implemented in Task 22)
 */
const AdDetailPage = lazy(() => import('../pages/dashboard/ad-manager/AdDetailPage'));

/**
 * Lazy-loaded Ad Create page
 */
const AdCreatePage = lazy(() => import('../pages/dashboard/ad-manager/AdCreatePage'));

/**
 * Lazy-loaded Ad Creatives page (shell — to be implemented in Task 22)
 */
const AdCreativesPage = lazy(() => import('../pages/dashboard/ad-manager/AdCreativesPage'));

/**
 * Lazy-loaded Ad Creative Create page (shell — to be implemented in Task 22)
 */
const AdCreativeCreatePage = lazy(() => import('../pages/dashboard/ad-manager/AdCreativeCreatePage'));

/**
 * Lazy-loaded Ad Creative Detail page
 */
const AdCreativeDetailPage = lazy(() => import('../pages/dashboard/ad-manager/AdCreativeDetailPage'));

/**
 * Lazy-loaded Ad Audiences page (shell — to be implemented in Task 23)
 */
const AdAudiencesPage = lazy(() => import('../pages/dashboard/ad-manager/AdAudiencesPage'));

/**
 * Lazy-loaded Ad Audience Create page (shell — to be implemented in Task 23)
 */
const AdAudienceCreatePage = lazy(() => import('../pages/dashboard/ad-manager/AdAudienceCreatePage'));

/**
 * Lazy-loaded Ad Insights page (shell — to be implemented in Task 24)
 */
const AdInsightsPage = lazy(() => import('../pages/dashboard/ad-manager/AdInsightsPage'));

/**
 * Lazy-loaded AI Ad Campaign Create page
 */
const AIAdCampaignCreatePage = lazy(() => import('../pages/dashboard/ad-manager/AIAdCampaignCreatePage'));

/**
 * Lazy-loaded AI Ad Execution Progress page
 */
const AIAdExecutionProgress = lazy(() => import('../pages/dashboard/ad-manager/components/AIAdExecutionProgress'));

/**
 * Lazy-loaded AI Ad Campaign Review page
 */
const AIAdCampaignReviewPage = lazy(() => import('../pages/dashboard/ad-manager/AIAdCampaignReviewPage'));

/**
 * Lazy-loaded AI Ad Performance page
 */
const AIAdPerformancePage = lazy(() => import('../pages/dashboard/ad-manager/AIAdPerformancePage'));

/**
 * Lazy-loaded AI Ad Re-Execution page
 */
const AIAdReExecutionPage = lazy(() => import('../pages/dashboard/ad-manager/AIAdReExecutionPage'));

/**
 * Lazy-loaded AI Execution Monitor page
 */
const AIExecutionMonitorPage = lazy(() => import('../pages/dashboard/ad-manager/AIExecutionMonitorPage'));

/**
 * Lazy-loaded Ad Executions list page
 */
const AdExecutionsPage = lazy(() => import('../pages/dashboard/ad-manager/AdExecutionsPage'));

/**
 * Lazy-loaded AI Ad Wizard page (100% AI-powered ad campaign creation)
 */
const AIAdWizardPage = lazy(() => import('../pages/dashboard/ad-manager/AIAdWizardPage'));

/**
 * Route transition animation variants
 */
const routeTransitionVariants = {
  initial: {
    opacity: 0,
    y: 8,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

/**
 * Loading Fallback
 */
const PageLoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-900">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-dark-500 dark:text-dark-400">Loading...</p>
    </div>
  </div>
);

/**
 * Dashboard page wrapper configuration
 */
interface DashboardPageConfig {
  pageTitle: string;
  pageDescription?: string;
  activePath: string;
  breadcrumbs: { label: string; href?: string }[];
  element: React.ReactNode;
  pageActions?: React.ReactNode;
}

const dashboardPages: DashboardPageConfig[] = [
  {
    pageTitle: 'Dashboard',
    pageDescription: "Welcome back! Here's an overview of your social media performance.",
    activePath: ROUTES.DASHBOARD,
    breadcrumbs: [{ label: 'Dashboard' }],
    element: <DashboardPage />,
  },
  {
    pageTitle: 'Workspaces',
    pageDescription: 'Manage your workspaces and team access.',
    activePath: ROUTES.DASHBOARD_WORKSPACES,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Workspaces' }],
    element: <WorkspacesPage />,
    pageActions: <WorkspacesPageActions />,
  },
  {
    pageTitle: 'Channels',
    pageDescription: 'Manage your connected social media accounts.',
    activePath: ROUTES.DASHBOARD_CHANNELS,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Channels' }],
    element: <ChannelsPage />,
    pageActions: <ChannelsPageActions />,
  },
  {
    pageTitle: 'Posts',
    pageDescription: 'Create, manage, and schedule your social media posts.',
    activePath: ROUTES.DASHBOARD_POSTS,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Posts' }],
    element: <PostsPage />,
  },
  {
    pageTitle: 'Calendar',
    pageDescription: 'View and manage your content schedule.',
    activePath: ROUTES.DASHBOARD_CALENDAR,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Calendar' }],
    element: <CalendarPage />,
  },
  {
    pageTitle: 'Media Library',
    pageDescription: 'Manage your images, videos, and other media assets.',
    activePath: ROUTES.DASHBOARD_MEDIA,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Media Library' }],
    element: <MediaPage />,
  },
  {
    pageTitle: 'Campaigns',
    pageDescription: 'Plan and manage your marketing campaigns.',
    activePath: ROUTES.DASHBOARD_CAMPAIGNS,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Campaigns' }],
    element: <CampaignsPage />,
    pageActions: <CampaignsPageActions />,
  },
  {
    pageTitle: 'Mentions',
    pageDescription: 'Monitor brand mentions across social platforms.',
    activePath: ROUTES.DASHBOARD_MENTIONS,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Mentions' }],
    element: <MentionsPage />,
  },
  {
    pageTitle: 'Analytics',
    pageDescription: 'Track performance and gain insights from your social data.',
    activePath: ROUTES.DASHBOARD_ANALYTICS,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Analytics' }],
    element: <AnalyticsPage />,
  },
  {
    pageTitle: 'Knowledge Base',
    pageDescription: 'Manage your company knowledge documents for AI training.',
    activePath: ROUTES.DASHBOARD_KNOWLEDGE,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Knowledge Base' }],
    element: <KnowledgePage />,
    pageActions: <KnowledgePageActions />,
  },
  {
    pageTitle: 'Brand Voice',
    pageDescription: 'Define and manage brand voice profiles for AI content generation.',
    activePath: ROUTES.DASHBOARD_BRAND_VOICE,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Brand Voice' }],
    element: <BrandVoicePage />,
    pageActions: <BrandVoicePageActions />,
  },
  {
    pageTitle: 'Audience Personas',
    pageDescription: 'Define target audience segments for personalized AI content.',
    activePath: ROUTES.DASHBOARD_PERSONAS,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Personas' }],
    element: <PersonasPage />,
    pageActions: <PersonasPageActions />,
  },
  {
    pageTitle: 'AI Agents',
    pageDescription: 'Monitor and overview your AI agent configurations and performance.',
    activePath: ROUTES.DASHBOARD_AI_AGENTS,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'AI Agents' }],
    element: <AgentsDashboardPage />,
  },
  {
    pageTitle: 'Settings',
    pageDescription: 'Configure your workspace and application settings.',
    activePath: ROUTES.DASHBOARD_SETTINGS,
    breadcrumbs: [{ label: 'Dashboard', href: ROUTES.DASHBOARD }, { label: 'Settings' }],
    element: <SettingsPage />,
  },
  {
    pageTitle: 'Team Members',
    pageDescription: 'Manage workspace members, roles, and permissions.',
    activePath: ROUTES.DASHBOARD_SETTINGS_TEAM,
    breadcrumbs: [
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Settings', href: ROUTES.DASHBOARD_SETTINGS },
      { label: 'Team' },
    ],
    element: <TeamPage />,
    pageActions: <TeamPageActions />,
  },
];

/**
 * DashboardPageWrapper
 */
const DashboardPageWrapper: React.FC<DashboardPageConfig> = ({
  pageTitle,
  pageDescription,
  activePath,
  breadcrumbs,
  element,
  pageActions,
}) => (
  <DashboardLayout
    pageTitle={pageTitle}
    pageDescription={pageDescription}
    activePath={activePath}
    breadcrumbs={breadcrumbs}
    pageActions={pageActions}
  >
    {element}
  </DashboardLayout>
);

/**
 * ProfileDashboardWrapper
 */
const ProfileDashboardWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <DashboardLayout
    pageTitle="Profile"
    pageDescription="Manage your personal profile and preferences."
    activePath={ROUTES.DASHBOARD_PROFILE}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Profile' },
    ]}
  >
    {children}
  </DashboardLayout>
);

/**
 * NotificationPreferencesDashboardWrapper
 */
const NotificationPreferencesDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Notification Preferences"
    pageDescription="Control how and when you receive notifications."
    activePath={ROUTES.DASHBOARD_PROFILE_NOTIFICATIONS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Profile', href: ROUTES.DASHBOARD_PROFILE },
      { label: 'Notifications' },
    ]}
  >
    <NotificationPreferencesPage />
  </DashboardLayout>
);

/**
 * KnowledgeDetailDashboardWrapper
 */
const KnowledgeDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Document Detail"
    pageDescription="View document details and extracted chunks."
    activePath={ROUTES.DASHBOARD_KNOWLEDGE}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Knowledge Base', href: ROUTES.DASHBOARD_KNOWLEDGE },
      { label: 'Document Detail' },
    ]}
  >
    <KnowledgeDocumentDetailPage />
  </DashboardLayout>
);

/**
 * PersonaDetailDashboardWrapper
 */
const PersonaDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Persona Detail"
    pageDescription="View detailed information about this audience persona."
    activePath={ROUTES.DASHBOARD_PERSONAS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Personas', href: ROUTES.DASHBOARD_PERSONAS },
      { label: 'Persona Detail' },
    ]}
  >
    <PersonaDetailPage />
  </DashboardLayout>
);

/**
 * BrandVoiceFormDashboardWrapper
 */
const BrandVoiceFormDashboardWrapper: React.FC<{ isEdit?: boolean }> = ({ isEdit }) => (
  <DashboardLayout
    pageTitle={isEdit ? 'Edit Brand Voice' : 'Create Brand Voice'}
    pageDescription={
      isEdit
        ? 'Update your brand voice profile configuration.'
        : 'Define a new brand voice for AI content generation.'
    }
    activePath={ROUTES.DASHBOARD_BRAND_VOICE}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Brand Voice', href: ROUTES.DASHBOARD_BRAND_VOICE },
      { label: isEdit ? 'Edit' : 'Create' },
    ]}
  >
    <BrandVoiceFormPage />
  </DashboardLayout>
);

/**
 * PersonaFormDashboardWrapper
 */
const PersonaFormDashboardWrapper: React.FC<{ isEdit?: boolean }> = ({ isEdit }) => (
  <DashboardLayout
    pageTitle={isEdit ? 'Edit Persona' : 'Create Persona'}
    pageDescription={
      isEdit
        ? 'Update your audience persona configuration.'
        : 'Define a new target audience persona for personalized content.'
    }
    activePath={ROUTES.DASHBOARD_PERSONAS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Personas', href: ROUTES.DASHBOARD_PERSONAS },
      { label: isEdit ? 'Edit' : 'Create' },
    ]}
  >
    <PersonaFormPage />
  </DashboardLayout>
);

/**
 * CampaignCreateSelectionDashboardWrapper
 */
const CampaignCreateSelectionDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Create Campaign"
    pageDescription="Choose how you'd like to create your campaign."
    activePath={ROUTES.DASHBOARD_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Campaigns', href: ROUTES.DASHBOARD_CAMPAIGNS },
      { label: 'Create' },
    ]}
  >
    <CampaignCreateSelectionPage />
  </DashboardLayout>
);

/**
 * CampaignCreateDashboardWrapper
 */
const CampaignCreateDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Create Campaign"
    pageDescription="Set up a new campaign with goals, audience targeting, and channel selection."
    activePath={ROUTES.DASHBOARD_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Campaigns', href: ROUTES.DASHBOARD_CAMPAIGNS },
      { label: 'Create' },
    ]}
  >
    <CampaignCreatePage />
  </DashboardLayout>
);

/**
 * AICampaignCreateDashboardWrapper
 */
const AICampaignCreateDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="AI Campaign Creation"
    pageDescription="Let AI generate a complete campaign strategy based on your business goals."
    activePath={ROUTES.DASHBOARD_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Campaigns', href: ROUTES.DASHBOARD_CAMPAIGNS },
      { label: 'AI Create' },
    ]}
  >
    <AICampaignCreatePage />
  </DashboardLayout>
);

/**
 * CampaignDetailDashboardWrapper
 */
const CampaignDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    activePath={ROUTES.DASHBOARD_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Campaigns', href: ROUTES.DASHBOARD_CAMPAIGNS },
      { label: 'Detail' },
    ]}
  >
    <CampaignDetailPage />
  </DashboardLayout>
);

/**
 * CampaignEditDashboardWrapper
 */
const CampaignEditDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Edit Campaign"
    pageDescription="Modify your campaign settings, goals, and content strategy."
    activePath={ROUTES.DASHBOARD_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Campaigns', href: ROUTES.DASHBOARD_CAMPAIGNS },
      { label: 'Edit' },
    ]}
  >
    <CampaignEditPage />
  </DashboardLayout>
);

/**
 * CampaignComparisonDashboardWrapper
 */
const CampaignComparisonDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Compare Campaigns"
    pageDescription="Compare performance metrics and strategies across campaigns."
    activePath={ROUTES.DASHBOARD_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Campaigns', href: ROUTES.DASHBOARD_CAMPAIGNS },
      { label: 'Compare' },
    ]}
  >
    <CampaignComparisonPage />
  </DashboardLayout>
);

/**
 * ContentDashboardWrapper
 */
const ContentDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Content"
    pageDescription="Create, manage, and schedule your social media content posts."
    activePath={ROUTES.DASHBOARD_CONTENT}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Content' },
    ]}
    pageActions={<ContentPageActions />}
  >
    <ContentPage />
  </DashboardLayout>
);

/**
 * ContentCreateDashboardWrapper
 */
const ContentCreateDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Generate Content"
    pageDescription="Use AI to generate content tailored to your brand, audience, and knowledge base."
    activePath={ROUTES.DASHBOARD_CONTENT}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Content', href: ROUTES.DASHBOARD_CONTENT },
      { label: 'Generate' },
    ]}
  >
    <ContentCreatePage />
  </DashboardLayout>
);

/**
 * ContentDetailDashboardWrapper
 */
const ContentDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Content Detail"
    pageDescription="View and edit content post details, media, and version history."
    activePath={ROUTES.DASHBOARD_CONTENT}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Content', href: ROUTES.DASHBOARD_CONTENT },
      { label: 'Detail' },
    ]}
  >
    <ContentDetailPage />
  </DashboardLayout>
);

/**
 * ApprovalQueueDashboardWrapper
 */
const ApprovalQueueDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Approval Queue"
    pageDescription="Review and approve content before publishing."
    activePath={ROUTES.DASHBOARD_CONTENT_APPROVAL_QUEUE}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Content', href: ROUTES.DASHBOARD_CONTENT },
      { label: 'Approval Queue' },
    ]}
  >
    <ApprovalQueuePage />
  </DashboardLayout>
);

/**
 * PublishingDashboardWrapper
 */
const PublishingDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Publishing"
    pageDescription="Monitor and manage your publishing pipeline across all channels."
    activePath={ROUTES.DASHBOARD_PUBLISHING}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Publishing' },
    ]}
  >
    <PublishingPage />
  </DashboardLayout>
);

/**
 * TrendsDashboardWrapper
 */
const TrendsDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Trends & Alerts"
    pageDescription="Monitor real-time trends, opportunities, and crisis alerts across your social channels."
    activePath={ROUTES.DASHBOARD_TRENDS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Trends & Alerts' },
    ]}
  >
    <TrendsPage />
  </DashboardLayout>
);

/**
 * TrendDetailDashboardWrapper
 */
const TrendDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Trend Detail"
    pageDescription="View detailed trend analysis, source data, and knowledge connections."
    activePath={ROUTES.DASHBOARD_TRENDS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Trends & Alerts', href: ROUTES.DASHBOARD_TRENDS },
      { label: 'Trend Detail' },
    ]}
  >
    <TrendDetailPage />
  </DashboardLayout>
);

/**
 * TrendHistoryDashboardWrapper
 */
const TrendHistoryDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Trend History"
    pageDescription="Review past trends with outcome tracking and content creation history."
    activePath={ROUTES.DASHBOARD_TRENDS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Trends & Alerts', href: ROUTES.DASHBOARD_TRENDS },
      { label: 'History' },
    ]}
  >
    <TrendHistoryPage />
  </DashboardLayout>
);

/**
 * PlaybooksDashboardWrapper
 */
const PlaybooksDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Crisis Playbooks"
    pageDescription="Configure automated response plans for crisis scenarios."
    activePath={ROUTES.DASHBOARD_PLAYBOOKS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Trends & Alerts', href: ROUTES.DASHBOARD_TRENDS },
      { label: 'Playbooks' },
    ]}
  >
    <PlaybooksPage />
  </DashboardLayout>
);

/**
 * PlaybookFormDashboardWrapper
 */
const PlaybookFormDashboardWrapper: React.FC<{ isEdit?: boolean }> = ({ isEdit }) => (
  <DashboardLayout
    pageTitle={isEdit ? 'Edit Playbook' : 'Create Playbook'}
    pageDescription={
      isEdit
        ? 'Update your crisis response playbook configuration.'
        : 'Define trigger conditions and automated responses for crisis scenarios.'
    }
    activePath={ROUTES.DASHBOARD_PLAYBOOKS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Trends & Alerts', href: ROUTES.DASHBOARD_TRENDS },
      { label: 'Playbooks', href: ROUTES.DASHBOARD_PLAYBOOKS },
      { label: isEdit ? 'Edit' : 'Create' },
    ]}
  >
    <PlaybookFormPage />
  </DashboardLayout>
);

/**
 * CrisisListDashboardWrapper
 */
const CrisisListDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Crisis Events"
    pageDescription="Monitor and manage active crisis events across your social channels."
    activePath={ROUTES.DASHBOARD_TRENDS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Trends & Alerts', href: ROUTES.DASHBOARD_TRENDS },
      { label: 'Crisis Events' },
    ]}
  >
    <CrisisListPage />
  </DashboardLayout>
);

/**
 * CrisisDashboardWrapper
 */
const CrisisDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Crisis Dashboard"
    pageDescription="View detailed crisis event metrics, timeline, and response actions."
    activePath={ROUTES.DASHBOARD_TRENDS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Trends & Alerts', href: ROUTES.DASHBOARD_TRENDS },
      { label: 'Crisis Events', href: '/dashboard/trends/crisis' },
      { label: 'Dashboard' },
    ]}
  >
    <CrisisDashboardPage />
  </DashboardLayout>
);

/**
 * InboxDashboardWrapper
 */
const InboxDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Inbox"
    pageDescription="Manage incoming messages, comments, and mentions from all channels."
    activePath={ROUTES.DASHBOARD_INBOX}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Inbox' },
    ]}
  >
    <InboxPage />
  </DashboardLayout>
);

/**
 * NotificationCenterDashboardWrapper
 */
const NotificationCenterDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Notification Center"
    pageDescription="View and manage all your workspace notifications."
    activePath={ROUTES.DASHBOARD_NOTIFICATIONS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Notifications' },
    ]}
  >
    <NotificationCenterPage />
  </DashboardLayout>
);

/**
 * ReportsDashboardWrapper
 */
const ReportsDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Reports"
    pageDescription="Create, manage, and schedule analytics reports to track your social media performance."
    activePath={ROUTES.DASHBOARD_REPORTS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Reports' },
    ]}
    pageActions={<ReportsPageActions />}
  >
    <ReportsPage />
  </DashboardLayout>
);

/**
 * ReportBuilderNewDashboardWrapper
 */
const ReportBuilderNewDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Report Builder"
    pageDescription="Create a custom report with drag-and-drop widgets."
    activePath={ROUTES.DASHBOARD_REPORTS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Reports', href: ROUTES.DASHBOARD_REPORTS },
      { label: 'New Report' },
    ]}
  >
    <ReportBuilderPage />
  </DashboardLayout>
);

/**
 * ReportBuilderEditDashboardWrapper
 */
const ReportBuilderEditDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Edit Report"
    pageDescription="Modify your report configuration and widgets."
    activePath={ROUTES.DASHBOARD_REPORTS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Reports', href: ROUTES.DASHBOARD_REPORTS },
      { label: 'Edit Report' },
    ]}
  >
    <ReportBuilderPage />
  </DashboardLayout>
);

/**
 * ReportViewerDashboardWrapper
 */
const ReportViewerDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="View Report"
    pageDescription="View generated report with AI insights, analytics data, and download options."
    activePath={ROUTES.DASHBOARD_REPORTS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Reports', href: ROUTES.DASHBOARD_REPORTS },
      { label: 'View Report' },
    ]}
  >
    <ReportViewerPage />
  </DashboardLayout>
);

/**
 * AgentConfigDashboardWrapper
 */
const AgentConfigDashboardWrapper: React.FC = () => (
  <DashboardLayout
    activePath={ROUTES.DASHBOARD_AI_AGENTS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'AI Agents', href: ROUTES.DASHBOARD_AI_AGENTS },
      { label: 'Configure' },
    ]}
  >
    <AgentConfigPage />
  </DashboardLayout>
);

/**
 * AgentLogsDashboardWrapper
 */
const AgentLogsDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Agent Logs"
    pageDescription="View detailed execution logs for all AI agent actions."
    activePath={ROUTES.DASHBOARD_AI_AGENTS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'AI Agents', href: ROUTES.DASHBOARD_AI_AGENTS },
      { label: 'Logs' },
    ]}
  >
    <AgentLogsPage />
  </DashboardLayout>
);

/**
 * AdminDashboardWrapper
 */
const AdminDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Administration"
    pageDescription="Manage your workspace settings, team, and configurations."
    activePath="/dashboard/admin"
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Administration' },
    ]}
  >
    <AdminPage />
  </DashboardLayout>
);

/**
 * ApprovalWorkflowsDashboardWrapper
 */
const ApprovalWorkflowsDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Approval Workflows"
    pageDescription="Configure multi-step approval pipelines for content, campaigns, and more."
    activePath="/dashboard/admin/approval-workflows"
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Administration', href: '/dashboard/admin' },
      { label: 'Approval Workflows' },
    ]}
  >
    <ApprovalWorkflowsPage />
  </DashboardLayout>
);

/**
 * ApprovalWorkflowFormDashboardWrapper
 */
const ApprovalWorkflowFormDashboardWrapper: React.FC<{ isEdit?: boolean }> = ({ isEdit }) => (
  <DashboardLayout
    pageTitle={isEdit ? 'Edit Approval Workflow' : 'Create Approval Workflow'}
    pageDescription={
      isEdit
        ? 'Modify your approval workflow configuration.'
        : 'Define a new multi-step approval pipeline.'
    }
    activePath="/dashboard/admin/approval-workflows"
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Administration', href: '/dashboard/admin' },
      { label: 'Approval Workflows', href: '/dashboard/admin/approval-workflows' },
      { label: isEdit ? 'Edit' : 'Create' },
    ]}
  >
    <ApprovalWorkflowFormPage />
  </DashboardLayout>
);

/**
 * DataGovernanceDashboardWrapper
 */
const DataGovernanceDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Data Governance"
    pageDescription="Manage data retention policies, compliance standards, and access controls."
    activePath="/dashboard/admin/data-governance"
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Administration', href: '/dashboard/admin' },
      { label: 'Data Governance' },
    ]}
  >
    <React.Suspense fallback={<div>Loading...</div>}>
      <DataGovernancePage />
    </React.Suspense>
  </DashboardLayout>
);

/**
 * APIKeysDashboardWrapper
 */
const APIKeysDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="API Keys"
    pageDescription="Manage developer API keys for external integrations and programmatic access."
    activePath="/dashboard/admin/api-keys"
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Administration', href: '/dashboard/admin' },
      { label: 'API Keys' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <APIKeysPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * WebhooksDashboardWrapper
 */
const WebhooksDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Webhooks"
    pageDescription="Manage outbound webhook subscriptions for external event delivery."
    activePath="/dashboard/admin/webhooks"
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Administration', href: '/dashboard/admin' },
      { label: 'Webhooks' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <WebhooksPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * SecuritySettingsDashboardWrapper
 */
const SecuritySettingsDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Security Settings"
    pageDescription="Configure workspace security policies, authentication requirements, and access controls."
    activePath={ROUTES.DASHBOARD_SETTINGS_SECURITY}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Settings', href: ROUTES.DASHBOARD_SETTINGS },
      { label: 'Security' },
    ]}
  >
    <SecuritySettingsPage />
  </DashboardLayout>
);

/**
 * BillingDashboardWrapper
 */
const BillingDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Billing & Subscription"
    pageDescription="Manage your workspace subscription, payment methods, and invoices."
    activePath={ROUTES.DASHBOARD_SETTINGS_BILLING}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Settings', href: ROUTES.DASHBOARD_SETTINGS },
      { label: 'Billing' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <BillingPage />
    </Suspense>
  </DashboardLayout>
);

/* ==================== Ad Manager Dashboard Wrappers ==================== */

/**
 * AdManagerDashboardWrapper
 */
const AdManagerDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Manager"
    pageDescription="Manage your paid advertising campaigns, creatives, audiences, and performance."
    activePath={ROUTES.DASHBOARD_AD_MANAGER}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager' },
    ]}
    pageActions={<AdManagerPageActions />}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdManagerPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdAccountsDashboardWrapper
 */
const AdAccountsDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Accounts"
    pageDescription="Connect and manage your advertising platform accounts."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_ACCOUNTS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Accounts' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdAccountsPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdAccountDetailDashboardWrapper
 */
const AdAccountDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Account Detail"
    pageDescription="View ad account details, permissions, and spending summary."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_ACCOUNTS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Accounts', href: ROUTES.DASHBOARD_AD_MANAGER_ACCOUNTS },
      { label: 'Detail' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdAccountDetailPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdCampaignsDashboardWrapper
 */
const AdCampaignsDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Campaigns"
    pageDescription="Create and manage your paid advertising campaigns."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Campaigns' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdCampaignsPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdCampaignCreateDashboardWrapper
 */
const AdCampaignCreateDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Create Ad Campaign"
    pageDescription="Set up a new paid advertising campaign with objective, budget, and targeting."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Campaigns', href: ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS },
      { label: 'Create' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdCampaignCreatePage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdCampaignDetailDashboardWrapper
 */
const AdCampaignDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Campaign Detail"
    pageDescription="View campaign performance, ad sets, and insights."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Campaigns', href: ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS },
      { label: 'Detail' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdCampaignDetailPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdCampaignEditDashboardWrapper
 */
const AdCampaignEditDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Edit Ad Campaign"
    pageDescription="Modify your ad campaign settings, budget, and schedule."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Campaigns', href: ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS },
      { label: 'Edit' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdCampaignEditPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdSetCreateDashboardWrapper
 */
const AdSetCreateDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Create Ad Set"
    pageDescription="Configure targeting, placements, budget, and schedule for your ad set."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Ad Sets' },
      { label: 'Create' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdSetCreatePage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdSetDetailDashboardWrapper
 */
const AdSetDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Set Detail"
    pageDescription="View ad set targeting, placements, budget, and performance."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Ad Set Detail' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdSetDetailPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdDetailDashboardWrapper
 */
const AdDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Detail"
    pageDescription="View individual ad creative, review status, and delivery insights."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Ad Detail' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdDetailPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdCreateDashboardWrapper
 */
const AdCreateDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Create Ad"
    pageDescription="Assemble a new ad by selecting a creative and ad set."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Ads' },
      { label: 'Create' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdCreatePage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdCreativesDashboardWrapper
 */
const AdCreativesDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Creatives"
    pageDescription="Manage your ad creative library and assets."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CREATIVES}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Creatives' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdCreativesPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdCreativeCreateDashboardWrapper
 */
const AdCreativeCreateDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Create Ad Creative"
    pageDescription="Design a new ad creative with media, copy, and call-to-action."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CREATIVES}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Creatives', href: ROUTES.DASHBOARD_AD_MANAGER_CREATIVES },
      { label: 'Create' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdCreativeCreatePage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdCreativeDetailDashboardWrapper
 */
const AdCreativeDetailDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Creative Detail"
    pageDescription="View ad creative details, preview, linked ads, and performance."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_CREATIVES}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Creatives', href: ROUTES.DASHBOARD_AD_MANAGER_CREATIVES },
      { label: 'Detail' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdCreativeDetailPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdAudiencesDashboardWrapper
 */
const AdAudiencesDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Audiences"
    pageDescription="Build and manage target audiences for your ad campaigns."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_AUDIENCES}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Audiences' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdAudiencesPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdAudienceCreateDashboardWrapper
 */
const AdAudienceCreateDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Create Audience"
    pageDescription="Define a new target audience for your ad campaigns."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_AUDIENCES}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Audiences', href: ROUTES.DASHBOARD_AD_MANAGER_AUDIENCES },
      { label: 'Create' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdAudienceCreatePage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdInsightsDashboardWrapper
 */
const AdInsightsDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Insights"
    pageDescription="Analyze ad performance metrics, spend trends, and ROI across your campaigns."
    activePath={ROUTES.DASHBOARD_AD_MANAGER_INSIGHTS}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Insights' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdInsightsPage />
    </Suspense>
  </DashboardLayout>
);

/* ==================== AI Ad Manager Dashboard Wrappers ==================== */

/**
 * AIAdCampaignCreateDashboardWrapper
 */
const AIAdCampaignCreateDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="AI Ad Campaign"
    pageDescription="Let AI agents research, plan, and build your ad campaign end-to-end."
    activePath={ROUTES.DASHBOARD_AD_MANAGER}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'AI Campaign' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AIAdCampaignCreatePage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AdExecutionsDashboardWrapper
 */
const AdExecutionsDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Ad Executions"
    pageDescription="View and manage AI ad campaign executions."
    activePath={ROUTES.DASHBOARD_AD_MANAGER}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Executions' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AdExecutionsPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AIAdExecutionProgressDashboardWrapper
 */
const AIAdExecutionProgressDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Execution Progress"
    pageDescription="Track real-time progress of AI ad campaign execution."
    activePath={ROUTES.DASHBOARD_AD_MANAGER}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Executions', href: '/dashboard/ad-manager/executions' },
      { label: 'Progress' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AIAdExecutionProgress />
    </Suspense>
  </DashboardLayout>
);

/**
 * AIAdCampaignReviewDashboardWrapper
 */
const AIAdCampaignReviewDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Campaign Review"
    pageDescription="Review and approve the AI-generated ad campaign before deployment."
    activePath={ROUTES.DASHBOARD_AD_MANAGER}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Executions', href: '/dashboard/ad-manager/executions' },
      { label: 'Review' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AIAdCampaignReviewPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AIAdPerformanceDashboardWrapper
 */
const AIAdPerformanceDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Execution Performance"
    pageDescription="Performance metrics and insights for AI ad campaign execution."
    activePath={ROUTES.DASHBOARD_AD_MANAGER}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Executions', href: '/dashboard/ad-manager/executions' },
      { label: 'Performance' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AIAdPerformancePage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AIAdReExecutionDashboardWrapper
 */
const AIAdReExecutionDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Re-Execute Campaign"
    pageDescription="Adjust parameters and re-execute the AI ad campaign pipeline."
    activePath={ROUTES.DASHBOARD_AD_MANAGER}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Executions', href: '/dashboard/ad-manager/executions' },
      { label: 'Re-Execute' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AIAdReExecutionPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AIExecutionMonitorDashboardWrapper
 */
const AIExecutionMonitorDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="Execution Monitor"
    pageDescription="Real-time monitoring of all AI ad campaign executions."
    activePath={ROUTES.DASHBOARD_AD_MANAGER}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'Execution Monitor' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AIExecutionMonitorPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AIAdWizardDashboardWrapper — 100% AI-powered ad campaign creation wizard
 */
const AIAdWizardDashboardWrapper: React.FC = () => (
  <DashboardLayout
    pageTitle="AI Ad Campaign Wizard"
    pageDescription="100% AI-powered ad campaign creation — from business goal to live campaign."
    activePath={ROUTES.DASHBOARD_AD_MANAGER}
    breadcrumbs={[
      { label: 'Dashboard', href: ROUTES.DASHBOARD },
      { label: 'Ad Manager', href: ROUTES.DASHBOARD_AD_MANAGER },
      { label: 'AI Wizard' },
    ]}
  >
    <Suspense fallback={<DashboardSkeleton />}>
      <AIAdWizardPage />
    </Suspense>
  </DashboardLayout>
);

/**
 * AnimatedRoutes
 */
const AnimatedRoutes: React.FC = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={routeTransitionVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <Routes location={location}>
          {/* ==================== Public Routes ==================== */}
          <Route path={ROUTES.HOME} element={<HomePage />} />
          <Route path={ROUTES.ABOUT} element={<AboutPage />} />
          <Route path={ROUTES.PRICING} element={<PricingPage />} />
          <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />
          <Route path={ROUTES.TERMS} element={<TermsPage />} />
          <Route path={ROUTES.CONTACT} element={<ContactPage />} />

          {/* ==================== Shared Report Viewer (Public) ==================== */}
          <Route path={ROUTES.SHARED_REPORT} element={<SharedReportViewerPage />} />

          {/* ==================== Auth Routes (Public Only) ==================== */}
          <Route element={<PublicOnlyRoute />}>
            <Route path={ROUTES.SIGN_IN} element={<SignInPage />} />
            <Route path={ROUTES.SIGN_UP} element={<SignUpPage />} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
            <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
          </Route>

          {/* ==================== MFA Challenge (Partial Auth State) ==================== */}
          <Route path={ROUTES.MFA_CHALLENGE} element={<MfaChallengePage />} />

          {/* ==================== Email Verification (Accessible to All) ==================== */}
          <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailPage />} />

          {/* ==================== OAuth Callback (Accessible to All) ==================== */}
          <Route path={ROUTES.OAUTH_CALLBACK} element={<OAuthCallbackPage />} />

          {/* ==================== Invitation Accept (Accessible to All) ==================== */}
          <Route path={ROUTES.INVITATION_ACCEPT} element={<InvitationAcceptPage />} />

          {/* ==================== Protected Dashboard Routes ==================== */}
          <Route element={<ProtectedRoute />}>
            {/* Email verification banner wraps all dashboard routes */}
            <Route element={<RequireEmailVerification />}>
              {/* Standard dashboard pages (excluding Inbox which has custom wrapper) */}
              {dashboardPages.map((config) => (
                <Route
                  key={config.activePath}
                  path={config.activePath}
                  element={<DashboardPageWrapper {...config} />}
                />
              ))}

              {/* Inbox page with custom three-panel layout */}
              <Route
                path={ROUTES.DASHBOARD_INBOX}
                element={<InboxDashboardWrapper />}
              />

              {/* Notification Center page */}
              <Route
                path={ROUTES.DASHBOARD_NOTIFICATIONS}
                element={<NotificationCenterDashboardWrapper />}
              />

              {/* Report Builder - New (must be before reports list to avoid path capture) */}
              <Route
                path={ROUTES.DASHBOARD_REPORT_NEW}
                element={<ReportBuilderNewDashboardWrapper />}
              />

              {/* Report Builder - Edit (must be before reports list) */}
              <Route
                path={ROUTES.DASHBOARD_REPORT_EDIT}
                element={<ReportBuilderEditDashboardWrapper />}
              />

              {/* Report Viewer (must be before reports list to avoid capture) */}
              <Route
                path={ROUTES.DASHBOARD_REPORT_VIEW}
                element={<ReportViewerDashboardWrapper />}
              />

              {/* Reports page (lazy-loaded) */}
              <Route
                path={ROUTES.DASHBOARD_REPORTS}
                element={<ReportsDashboardWrapper />}
              />

              {/* Trend History page (must be before :trendId to avoid path capture) */}
              <Route
                path={ROUTES.DASHBOARD_TREND_HISTORY}
                element={<TrendHistoryDashboardWrapper />}
              />

              {/* Crisis Events pages (must be before generic trends routes to avoid path conflicts) */}
              <Route
                path="/dashboard/trends/crisis/:crisisEventId"
                element={<CrisisDashboardWrapper />}
              />
              <Route
                path="/dashboard/trends/crisis"
                element={<CrisisListDashboardWrapper />}
              />

              {/* Crisis Playbooks pages (must be before generic trends routes) */}
              <Route
                path={ROUTES.DASHBOARD_PLAYBOOK_NEW}
                element={<PlaybookFormDashboardWrapper />}
              />
              <Route
                path={ROUTES.DASHBOARD_PLAYBOOK_EDIT}
                element={<PlaybookFormDashboardWrapper isEdit />}
              />
              <Route
                path={ROUTES.DASHBOARD_PLAYBOOKS}
                element={<PlaybooksDashboardWrapper />}
              />

              {/* Trend Detail page (must be after /history, /crisis, /playbooks) */}
              <Route
                path={ROUTES.DASHBOARD_TREND_DETAIL}
                element={<TrendDetailDashboardWrapper />}
              />

              {/* Trends & Alerts page (lazy-loaded) */}
              <Route
                path={ROUTES.DASHBOARD_TRENDS}
                element={<TrendsDashboardWrapper />}
              />

              {/* Publishing page (lazy-loaded) */}
              <Route
                path={ROUTES.DASHBOARD_PUBLISHING}
                element={<PublishingDashboardWrapper />}
              />

              {/* Content Create page (must be before generic content route) */}
              <Route
                path={ROUTES.DASHBOARD_CONTENT_NEW}
                element={<ContentCreateDashboardWrapper />}
              />

              {/* Approval Queue page (must be before :postId to avoid capturing 'approval-queue' as a postId) */}
              <Route
                path={ROUTES.DASHBOARD_CONTENT_APPROVAL_QUEUE}
                element={<ApprovalQueueDashboardWrapper />}
              />

              {/* Content Detail page (must be after /new and /approval-queue but handles :postId) */}
              <Route
                path={ROUTES.DASHBOARD_CONTENT_DETAIL}
                element={<ContentDetailDashboardWrapper />}
              />

              {/* Content page (lazy-loaded) */}
              <Route
                path={ROUTES.DASHBOARD_CONTENT}
                element={<ContentDashboardWrapper />}
              />

              {/* Campaign Comparison page (must be before dynamic campaign routes) */}
              <Route
                path={ROUTES.DASHBOARD_CAMPAIGN_COMPARE}
                element={<CampaignComparisonDashboardWrapper />}
              />

              {/* Campaign Create Selection page (must be before dynamic campaign routes) */}
              <Route
                path={ROUTES.DASHBOARD_CAMPAIGNS_CREATE}
                element={<CampaignCreateSelectionDashboardWrapper />}
              />

              {/* AI Campaign Create page (must be before dynamic campaign routes) */}
              <Route
                path={ROUTES.DASHBOARD_CAMPAIGNS_AI_NEW}
                element={<AICampaignCreateDashboardWrapper />}
              />

              {/* Campaign Create page (must be before dynamic campaign routes) */}
              <Route
                path={ROUTES.DASHBOARD_CAMPAIGNS_NEW}
                element={<CampaignCreateDashboardWrapper />}
              />

              {/* Campaign Edit page */}
              <Route
                path={ROUTES.DASHBOARD_CAMPAIGN_EDIT}
                element={<CampaignEditDashboardWrapper />}
              />

              {/* Campaign Detail page */}
              <Route
                path={ROUTES.DASHBOARD_CAMPAIGN_DETAIL}
                element={<CampaignDetailDashboardWrapper />}
              />

              {/* Knowledge Document Detail page */}
              <Route
                path={ROUTES.DASHBOARD_KNOWLEDGE_DETAIL}
                element={<KnowledgeDetailDashboardWrapper />}
              />

              {/* Brand Voice Create page */}
              <Route
                path={ROUTES.DASHBOARD_BRAND_VOICE_NEW}
                element={<BrandVoiceFormDashboardWrapper />}
              />

              {/* Brand Voice Edit page */}
              <Route
                path={ROUTES.DASHBOARD_BRAND_VOICE_EDIT}
                element={<BrandVoiceFormDashboardWrapper isEdit />}
              />

              {/* Persona Create page (must be before :personaId to avoid path capture) */}
              <Route
                path={ROUTES.DASHBOARD_PERSONAS_NEW}
                element={<PersonaFormDashboardWrapper />}
              />

              {/* Persona Edit page (must be before :personaId to avoid path capture) */}
              <Route
                path={ROUTES.DASHBOARD_PERSONAS_EDIT}
                element={<PersonaFormDashboardWrapper isEdit />}
              />

              {/* Persona Detail page (must be after /new and /:personaId/edit) */}
              <Route
                path={ROUTES.DASHBOARD_PERSONA_DETAIL}
                element={<PersonaDetailDashboardWrapper />}
              />

              {/* Agent Logs page (must be before agent config to match before :agentType) */}
              <Route
                path={ROUTES.DASHBOARD_AI_AGENTS_LOGS}
                element={<AgentLogsDashboardWrapper />}
              />

              {/* Agent Configuration page */}
              <Route
                path={ROUTES.DASHBOARD_AI_AGENTS_CONFIG}
                element={<AgentConfigDashboardWrapper />}
              />

              {/* ==================== Ad Manager Routes (Owner/Admin/Manager only) ==================== */}
              <Route element={<RequireRole allowedRoles={['owner', 'admin', 'manager']} />}>
                {/* AI Ad Wizard — 100% AI-powered ad campaign creation (must be before other ad-manager routes) */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_AI_WIZARD}
                  element={<AIAdWizardDashboardWrapper />}
                />

                {/* AI Ad Campaign Create (must be before other ad-manager routes to avoid capture) */}
                <Route
                  path="/dashboard/ad-manager/ai-campaign"
                  element={<AIAdCampaignCreateDashboardWrapper />}
                />

                {/* AI Execution Monitor (must be before executions/:executionId to avoid capture) */}
                <Route
                  path="/dashboard/ad-manager/execution-monitor"
                  element={<AIExecutionMonitorDashboardWrapper />}
                />

                {/* AI Ad Execution - Review (must be before :executionId to avoid capture) */}
                <Route
                  path="/dashboard/ad-manager/executions/:executionId/review"
                  element={<AIAdCampaignReviewDashboardWrapper />}
                />

                {/* AI Ad Execution - Performance */}
                <Route
                  path="/dashboard/ad-manager/executions/:executionId/performance"
                  element={<AIAdPerformanceDashboardWrapper />}
                />

                {/* AI Ad Execution - Re-Execute */}
                <Route
                  path="/dashboard/ad-manager/executions/:executionId/re-execute"
                  element={<AIAdReExecutionDashboardWrapper />}
                />

                {/* AI Ad Execution Progress */}
                <Route
                  path="/dashboard/ad-manager/executions/:executionId"
                  element={<AIAdExecutionProgressDashboardWrapper />}
                />

                {/* Ad Executions list page */}
                <Route
                  path="/dashboard/ad-manager/executions"
                  element={<AdExecutionsDashboardWrapper />}
                />

                {/* Ad Manager landing page */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER}
                  element={<AdManagerDashboardWrapper />}
                />

                {/* Ad Accounts pages */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_ACCOUNT_DETAIL}
                  element={<AdAccountDetailDashboardWrapper />}
                />
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_ACCOUNTS}
                  element={<AdAccountsDashboardWrapper />}
                />

                {/* Ad Campaign Create (must be before :id to avoid path capture) */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGN_CREATE}
                  element={<AdCampaignCreateDashboardWrapper />}
                />

                {/* Ad Campaign Edit (must be before :id to avoid path capture) */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGN_EDIT}
                  element={<AdCampaignEditDashboardWrapper />}
                />

                {/* Ad Campaign Detail */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGN_DETAIL}
                  element={<AdCampaignDetailDashboardWrapper />}
                />

                {/* Ad Campaigns list */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_CAMPAIGNS}
                  element={<AdCampaignsDashboardWrapper />}
                />

                {/* Ad Set Create (must be before :id to avoid path capture) */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_AD_SET_CREATE}
                  element={<AdSetCreateDashboardWrapper />}
                />

                {/* Ad Set Detail */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_AD_SET_DETAIL}
                  element={<AdSetDetailDashboardWrapper />}
                />

                {/* Ad Create (must be before :id to avoid path capture) */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_AD_CREATE}
                  element={<AdCreateDashboardWrapper />}
                />

                {/* Ad Detail */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_AD_DETAIL}
                  element={<AdDetailDashboardWrapper />}
                />

                {/* Ad Creative Create (must be before :creativeId to avoid path capture) */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_CREATIVE_CREATE}
                  element={<AdCreativeCreateDashboardWrapper />}
                />

                {/* Ad Creative Detail (must be after /create but before creatives list) */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_CREATIVE_DETAIL}
                  element={<AdCreativeDetailDashboardWrapper />}
                />

                {/* Ad Creatives list */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_CREATIVES}
                  element={<AdCreativesDashboardWrapper />}
                />

                {/* Ad Audience Create (must be before audiences list) */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_AUDIENCE_CREATE}
                  element={<AdAudienceCreateDashboardWrapper />}
                />

                {/* Ad Audiences list */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_AUDIENCES}
                  element={<AdAudiencesDashboardWrapper />}
                />

                {/* Ad Insights dashboard */}
                <Route
                  path={ROUTES.DASHBOARD_AD_MANAGER_INSIGHTS}
                  element={<AdInsightsDashboardWrapper />}
                />
              </Route>

              {/* ==================== Settings Security & Billing (Owner/Admin only) ==================== */}
              <Route element={<RequireRole allowedRoles={['owner', 'admin']} />}>
                {/* Security Settings page */}
                <Route
                  path={ROUTES.DASHBOARD_SETTINGS_SECURITY}
                  element={<SecuritySettingsDashboardWrapper />}
                />

                {/* Billing page */}
                <Route
                  path={ROUTES.DASHBOARD_SETTINGS_BILLING}
                  element={<BillingDashboardWrapper />}
                />
              </Route>

              {/* ==================== Admin Routes (Owner/Admin only) ==================== */}
              <Route element={<RequireRole allowedRoles={['owner', 'admin']} />}>
                {/* Administration page */}
                <Route
                  path="/dashboard/admin"
                  element={<AdminDashboardWrapper />}
                />

                {/* Approval Workflow Form - New (must be before :workflowId to avoid path capture) */}
                <Route
                  path="/dashboard/admin/approval-workflows/new"
                  element={<ApprovalWorkflowFormDashboardWrapper />}
                />

                {/* Approval Workflow Form - Edit */}
                <Route
                  path="/dashboard/admin/approval-workflows/:workflowId/edit"
                  element={<ApprovalWorkflowFormDashboardWrapper isEdit />}
                />

                {/* Approval Workflows list page */}
                <Route
                  path="/dashboard/admin/approval-workflows"
                  element={<ApprovalWorkflowsDashboardWrapper />}
                />

                {/* Data Governance page */}
                <Route
                  path="/dashboard/admin/data-governance"
                  element={<DataGovernanceDashboardWrapper />}
                />

                {/* API Keys page */}
                <Route
                  path="/dashboard/admin/api-keys"
                  element={<APIKeysDashboardWrapper />}
                />

                {/* Webhooks page */}
                <Route
                  path="/dashboard/admin/webhooks"
                  element={<WebhooksDashboardWrapper />}
                />
              </Route>

              {/* Notification Preferences page (must be before profile to avoid nested route conflicts) */}
              <Route
                path={ROUTES.DASHBOARD_PROFILE_NOTIFICATIONS}
                element={<NotificationPreferencesDashboardWrapper />}
              />

              {/* Profile pages with nested tab routes */}
              <Route
                path={ROUTES.DASHBOARD_PROFILE}
                element={
                  <ProfileDashboardWrapper>
                    <ProfilePage />
                  </ProfileDashboardWrapper>
                }
              >
                <Route index element={<ProfileGeneralTab />} />
                <Route path="security" element={<ProfileSecurityTab />} />
                <Route path="preferences" element={<ProfilePreferencesTab />} />
                <Route path="sessions" element={<ProfileSessionsTab />} />
                <Route path="data-privacy" element={<ProfileDataPrivacyTab />} />
              </Route>
            </Route>
          </Route>

          {/* ==================== 404 Catch-All ==================== */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * AppRouter
 */
export const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <ScrollToTop />
      <AnimatedRoutes />
    </Suspense>
  );
};

AppRouter.displayName = 'AppRouter';